import React, { useContext, useEffect } from 'react'
import getConfig from 'next/config'
import styled from '@emotion/styled'
import { gql, useMutation } from '@apollo/client'
import cookies from 'js-cookie'
import { Alert, Button, space } from '@ticketswap/solar'
import { Facebook } from '@ticketswap/comets'
import { AUTHENTICATION_STARTED, AUTHENTICATION_COMPLETED } from '../../lib/tracking-events'
import { track, setTrackingIdentity } from '../../utils'
import getViewer from '../../lib/get-viewer'
import { GENERATE_AUTH_TOKEN } from './generateAuthToken'
import { useViewer } from '../ViewerContext'
import { FacebookContext } from '~/components/FacebookContext'

const { CLIENT_ID, TOKEN_COOKIE_NAME } = getConfig().publicRuntimeConfig

const AUTHENTICATE_USER_WITH_FACEBOOK = gql`
  mutation authenticateUserWithFacebook($input: AuthenticateFacebookUserInput!) {
    facebookAuth: authenticateUserWithFacebook(input: $input) {
      apiAccessToken {
        tokenString
        expiresAt
      }
      isNew
    }
  }
`

const StyledAlert = styled(Alert)`
  margin-bottom: ${space[8]};
`

const FacebookAuthButton = ({ children, t, onSignup, onLogin }) => {
  const [errorMessage, setErrorMessage] = React.useState(null)
  const [loadingFacebook, setLoadingFacebook] = React.useState(false)
  const { setViewer } = useViewer()
  const { initialize, isInitialized, login } = useContext(FacebookContext)
  const [generate, { client }] = useMutation(GENERATE_AUTH_TOKEN)
  const [authenticate] = useMutation(AUTHENTICATE_USER_WITH_FACEBOOK)

  useEffect(() => {
    if (!isInitialized) {
      initialize()
    }
  }, [initialize, isInitialized])

  const handleCompleted = async facebookData => {
    const {
      data: { authenticateClientForToken },
    } = await generate({
      variables: {
        input: {
          clientId: CLIENT_ID,
        },
      },
    })

    const {
      data: { facebookAuth },
    } = await authenticate({
      variables: {
        input: {
          token: authenticateClientForToken.token,
          facebookAccessToken: facebookData.accessToken,
        },
      },
    })
    const { tokenString } = facebookAuth.apiAccessToken

    cookies.set(TOKEN_COOKIE_NAME, tokenString, {
      expires: 30,
      secure: true,
      sameSite: 'lax',
    })

    const user = await getViewer(client)
    setTrackingIdentity(user)

    if (facebookAuth.isNew) {
      // Early return so we don’t call `resetStore()` in this case (Will be
      // called in `SignupSocial.js` instead)
      return onSignup({
        firstName: facebookData.profile.first_name,
        lastName: facebookData.profile.last_name,
      })
    }

    track(AUTHENTICATION_COMPLETED, {
      method: 'facebook',
      isNew: facebookAuth.isNew,
    })

    await client.resetStore()
    setViewer(user)
    onLogin()
  }

  const handleError = error => {
    setLoadingFacebook(false)
    let message = ''

    if (error.message === 'We were unable to authenticate you on Facebook') {
      message = t('auth.login.facebook.errors.not_authenticated')
    } else if (error.message === 'We were unable to retrieve your profile from Facebook') {
      message = t('auth.login.facebook.errors.not_authorized')
    } else {
      message = t('auth.login.facebook.errors.generic')
    }

    track(AUTHENTICATION_COMPLETED, {
      method: 'facebook',
      error: error.message,
    })

    setErrorMessage(message)
  }

  return (
    <>
      {errorMessage && <StyledAlert variant="error">{errorMessage}</StyledAlert>}
      <Button
        loading={loadingFacebook}
        disabled={!isInitialized}
        variant="facebook"
        fullWidth
        leftAdornment={<Facebook size={24} />}
        onClick={async () => {
          setLoadingFacebook(true)
          track(AUTHENTICATION_STARTED, {
            method: 'facebook',
          })

          try {
            const response = await login()
            await handleCompleted(response)
          } catch (error) {
            handleError(error)
          }
        }}
      >
        {children}
      </Button>
    </>
  )
}

export default FacebookAuthButton
